/**
 * TagFilterModule
 *
 * A jQuery-based module for creating and managing tag-based filtering UI.
 * Creates a dropdown filter interface with tags that can be added and removed,
 * with support for form submission and callbacks. Now includes search term integration.
 *
 * @namespace TagFilterModule
 */

/**
 * Initialize the tag filter module with the provided configuration.
 *
 * @function init
 * @memberof TagFilterModule
 * @param {Object} [config={}] - Configuration options for the tag filter
 * @param {string} [config.targetSelector='.tag-filter-wrapper'] - Selector for target elements to transform into tag filters
 * @param {string} [config.formSelector='form'] - Selector for the form within the target
 * @param {string} [config.applyBtnSelector='.btn-apply'] - Selector for the apply button
 * @param {string} [config.resetBtnSelector='.btn-reset'] - Selector for the reset button
 * @param {string} [config.tagSelectorsSelector='.tag-select'] - Selector for the tag select elements
 * @param {string} [config.searchInputSelector='input[name="term"]'] - Selector for the search input field
 * @param {string} [config.searchFormSelector='form[name="frmSearch"]'] - Selector for the search form
 * @param {boolean} [config.useDummyData=false] - Whether to use dummy data for testing
 * @param {boolean} [config.submitOnApply=true] - Whether to submit the form when apply is clicked
 * @param {boolean} [config.submitOnReset=true] - Whether to submit the form when reset is clicked
 * @param {boolean} [config.submitOnRemove=true] - Whether to submit the form when a tag is removed
 * @param {boolean} [config.closeMenuOnApply=true] - Whether to close the dropdown menu when apply is clicked
 * @param {boolean} [config.closeMenuOnReset=true] - Whether to close the dropdown menu when reset is clicked
 * @param {Function} [config.onInitComplete=null] - Callback function to execute when initialization is complete
 * @returns {Array} An array of initialized instances, each containing target, elements and config
 *
 * @example
 * // Basic initialization with default options
 * const instances = TagFilterModule.init();
 *
 * @example
 * // Initialization with custom options
 * const instances = TagFilterModule.init({
 *     targetSelector: '#my-filter',
 *     submitOnApply: false,
 *     onInitComplete: function(instances) {
 *         console.log('Initialization complete', instances);
 *     }
 * });
 */

/**
 * Manually populate tags based on the current state of selectors.
 *
 * @function populateTags
 * @memberof TagFilterModule
 * @param {Object} config - Configuration object containing selectors
 * @returns {number} The count of tags that were populated
 *
 * @example
 * // Manually update tags after changing select values programmatically
 * const tagCount = TagFilterModule.populateTags({
 *     applyBtnSelector: '.btn-apply',
 *     resetBtnSelector: '.btn-reset',
 *     tagSelectorsSelector: '.tag-select'
 * });
 */



const createTagFilterModule = function($) {

    const createDomStructure = (config, $targetElement) => {
        let $wrapper = $targetElement;

        // Find tag container or create it if it doesn't exist
        let $tagContainer = $wrapper.find('.tag-container');
        if ($tagContainer.length === 0) {
            $tagContainer = $('<div class="tag-container"></div>');
            $wrapper.append($tagContainer);
        }

        // Find primary tag form (first form in wrapper or specifically marked one)
        let $primaryForm = $wrapper.find('> form').first();
        if (config.primaryFormSelector) {
            const $specificForm = $wrapper.find(config.primaryFormSelector);
            if ($specificForm.length > 0) {
                $primaryForm = $specificForm;
            }
        }

        // Find secondary forms (all forms except the primary one)
        let $secondaryForms = $wrapper.find('> form').not($primaryForm);

        let $dropdown = $wrapper.find('.dropdown');
        if ($dropdown.length === 0) {
            const filterText = window.lang && window.lang['filter'] ? window.lang['filter'] : 'Filter';
            const filterOptionsText = window.lang && window.lang['filter_options'] ? window.lang['filter_options'] : 'Filter options';
            const resetText = window.lang && window.lang['label_reset'] ? window.lang['label_reset'] : 'Reset';
            const applyText = window.lang && window.lang['apply'] ? window.lang['apply'] : 'Apply';

            const dropdownHtml = `
                <div class="dropdown">
                    <a href="#" type="button" class="btn-filter dropdown-toggle"
                       data-bs-toggle="dropdown" data-bs-auto-close="outside" aria-expanded="false">
                        <i class="bi bi-funnel-fill"></i>
                        ${filterText}
                        <span class="badge bg-primary custom-badge">0</span>
                    </a>
                    <div class="dropdown-menu dropdown-menu-end">
                        <h6 class="dropdown-header">${filterOptionsText}</h6>
                        <div class="filter-form-container"></div>
                        <div class="secondary-forms-container"></div>
                        <div class="form-group px-3 mb-1">
                            <div class="d-flex justify-content-between">
                                <button type="button" class="btn btn-light btn-reset">${resetText}</button>
                                <button type="button" class="btn btn-primary btn-apply">${applyText}</button>
                            </div>
                        </div>
                    </div>
                </div>
            `;
            $wrapper.append(dropdownHtml);

            // Move primary form to the filter-form-container
            if ($primaryForm.length > 0) {
                const $formContainer = $wrapper.find('.filter-form-container');
                $formContainer.append($primaryForm);
            }

            // Move secondary forms to the secondary-forms-container
            if ($secondaryForms.length > 0) {
                const $secondaryContainer = $wrapper.find('.secondary-forms-container');
                $secondaryForms.each(function() {
                    // Add a divider between forms if not the first secondary form
                    if ($secondaryContainer.children().length > 0) {
                        $secondaryContainer.append('<div class="dropdown-divider"></div>');
                    }
                    $secondaryContainer.append($(this));
                });
            }
        }

        return {
            wrapper: $wrapper,
            tagContainer: $wrapper.find('.tag-container'),
            dropdown: $wrapper.find('.dropdown'),
            primaryForm: $primaryForm,
            secondaryForms: $secondaryForms,
            applyBtn: $wrapper.find(config.applyBtnSelector),
            resetBtn: $wrapper.find(config.resetBtnSelector),
            tagSelectors: $primaryForm.find(config.tagSelectorsSelector),
            badge: $wrapper.find('.custom-badge')
        };
    };

    /**
     * Get all the relevant DOM elements including search elements
     */
    const getDomElements = (config, $targetElement) => {
        let $primaryForm = $targetElement.find('> .dropdown > .dropdown-menu > .filter-form-container > form').first();
        if (!$primaryForm.length) {
            // Fallback to first form if not found in expected location
            $primaryForm = $targetElement.find('form').first();
        }

        return {
            tagForm: $primaryForm,
            applyBtn: $targetElement.find(config.applyBtnSelector),
            resetBtn: $targetElement.find(config.resetBtnSelector),
            tagContainer: $targetElement.find('.tag-container'),
            tagSelectors: $primaryForm.find(config.tagSelectorsSelector),
            badge: $targetElement.find('.custom-badge'),
            searchInput: $(config.searchInputSelector),
            searchForm: $(config.searchFormSelector)
        };
    };

    /**
     * Extract search term from current URL
     */
    const getSearchTermFromURL = () => {
        const urlParams = new URLSearchParams(window.location.search);
        return urlParams.get('term') || '';
    };

    /**
     * Remove search term from URL and reload page
     */
    const removeSearchTermFromURL = () => {
        const url = new URL(window.location);
        url.searchParams.delete('term');
        window.location.href = url.toString();
    };

    /**
     * Sync search input field with search term
     */
    const syncSearchInput = (elements, searchTerm = '') => {
        if (elements.searchInput.length > 0) {
            elements.searchInput.val(searchTerm);
        }
    };

    /**
     * Handle search term tags
     */
    const handleSearchTags = (elements) => {
        const searchTerm = getSearchTermFromURL();
        let count = 0;

        if (searchTerm && searchTerm.trim() !== '') {
            const tagId = 'search_' + Date.now(); // Unique ID for search tag
            count++;

            const searchTermLabel = window.lang && window.lang['search_term'] ? window.lang['search_term'] : 'Search';

            $(elements.tagContainer).append(
                `<span class="tag" data-value="${searchTerm}" data-selector="search" data-tag-id="${tagId}" data-type="search">
                    <span class="tag-key">${searchTermLabel} </span>: ${searchTerm.trim()} 
                    <span class="remove-tag"><i class="bi bi-x-lg"></i></span>
                </span>`
            );
        }

        return count;
    };
    /**
     * Populate all tags (filters + search)
     */
    const populateTags = (elements) => {
        $(elements.tagContainer).html("");
        let count = 0;

        // Handle filter selector tags
        elements.tagSelectors.each(function() {
            const selectedValue = $(this).val();
            if (!selectedValue || selectedValue === '-1' || selectedValue === '') return;

            const selectedText = $(this).find("option:selected").text();
            const tagId = $(this).attr('name') + '_' + selectedValue;

            // Split on : to separate key and value
            const textParts = selectedText.split(':');
            let tagKey, tagValue;

            if (textParts.length > 1) {
                tagKey = textParts[0].trim();
                tagValue = textParts.slice(1).join(':').trim();
            } else {
                // Fallback if no colon found
                tagKey = $(this).closest('.form-group').find('label').text().replace(':', '') || $(this).attr('name');
                tagValue = selectedText;
            }

            count++;
            $(elements.tagContainer).append(
                `<span class="tag" data-value="${selectedValue}" data-selector="${$(this).attr('name')}" data-tag-id="${tagId}" data-type="filter">
                    <span class="tag-key">${tagKey}:</span> ${tagValue} 
                    <span class="remove-tag"><i class="bi bi-x-lg"></i></span>
                </span>`
            );
        });

        // Handle search tags
        count += handleSearchTags(elements);

        $(elements.badge).text(count);
        if (count === 0) {
            $(elements.badge).hide();
        } else {
            $(elements.badge).show();
        }

        return count;
    };

    /**
     * Process form action and execute callback if needed
     */
    const processFormAction = (elements) => {
        const $form = elements.tagForm;
        const callback = $form.data("callback");
        let result = false;

        if (callback) {
            try {
                if (typeof window[callback] === "function") {
                    result = window[callback]($form);
                }
            } catch (e) {
                console.warn('Error executing callback:', e);
            }
        }

        if (!callback) {

            const action = $form.attr('action') || window.location.pathname;
            // Create combined form data
            const combinedData = new URLSearchParams();

            // Add filter form data (excluding empty values)
            $form.find('input, select, textarea').each(function() {
                const $input = $(this);
                const name = $input.attr('name');
                const value = $input.val();

                if (name && value && value.trim() !== '') {
                    combinedData.append(name, value);
                }
            });

            // Add search term from search form
            const $searchInput = $('input[name="term"]');
            if ($searchInput.length > 0) {
                const searchTerm = $searchInput.val();
                if (searchTerm && searchTerm.trim() !== '') {
                    combinedData.append('term', searchTerm.trim());
                }
            }

            // Add any other parameters from current URL that we want to preserve
            const currentParams = new URLSearchParams(window.location.search);
            const preserveParams = ['id']; // Add other params you want to preserve

            preserveParams.forEach(param => {
                if (currentParams.has(param) && !combinedData.has(param)) {
                    combinedData.append(param, currentParams.get(param));
                }
            });

            let finalUrl = action;
            if (combinedData.toString()) {
                finalUrl += (action.includes('?') ? '&' : '?') + combinedData.toString();
            }

            window.location.href = finalUrl;
            result = true;
        }

        return result;
    };
    /**
     * Handle tag removal based on tag type
     */
    const handleTagRemoval = (elements, config, $tag) => {
        const tagType = $tag.attr('data-type');
        const selector = $tag.attr('data-selector');

        if (tagType === 'search') {
            // Clear search input and reload without search term
            syncSearchInput(elements, '');
            removeSearchTermFromURL();
        } else {
            // Handle filter tags normally
            $(`select[name="${selector}"]`, elements.tagForm).val('').trigger('change');
            populateTags(elements);

            if (config.submitOnRemove) {
                processFormAction(elements, config);
            }
        }
    };

    /**
     * Handle reset functionality for both filters and search
     */
    const handleReset = (elements, config) => {
        // Reset filter selectors
        elements.tagSelectors.each(function() {
            $(this).val("").trigger('change');
        });

        // Clear search input
        syncSearchInput(elements, '');

        // Clear tags container
        $(elements.tagContainer).html("");

        // Reset badge
        $(elements.badge).text(0).hide();

        if (config.submitOnReset) {
            // If there was a search term, we need to remove it from URL
            const searchTerm = getSearchTermFromURL();
            if (searchTerm && searchTerm.trim() !== '') {
                removeSearchTermFromURL();
            } else {
                processFormAction(elements, config);
            }
        }
    };

    /**
     * Set up event handlers for the tag filter
     */
    const setupEventHandlers = (elements, config) => {
        const closeDropdown = (element) => {
            if (typeof bootstrap !== 'undefined') {
                try {
                    const dropdownMenu = $(element).closest('.dropdown-menu');
                    const dropdownToggle = dropdownMenu.closest('.dropdown').find('[data-bs-toggle="dropdown"]')[0];
                    if (dropdownToggle) {
                        const dropdown = bootstrap.Dropdown.getInstance(dropdownToggle);
                        if (dropdown) dropdown.hide();
                    }
                } catch (err) {
                    console.warn('Bootstrap dropdown could not be closed automatically', err);
                }
            }
        };

        $(elements.applyBtn).off('click').on('click', function(e) {
            e.preventDefault();
            populateTags(elements);

            if (config.submitOnApply) {
                processFormAction(elements, config);
            }

            if (config.closeMenuOnApply) {
                closeDropdown(this);
            }
        });

        $(elements.resetBtn).off('click').on('click', function(e) {
            e.preventDefault();
            e.stopPropagation();

            handleReset(elements, config);

            if (config.closeMenuOnReset) {
                closeDropdown(this);
            }
        });

        $(elements.tagContainer).off('click', '.remove-tag').on('click', '.remove-tag', function() {
            const $tag = $(this).parent('.tag');
            handleTagRemoval(elements, config, $tag);
        });

        // Optional: Add search form submission handler to create tags
        if (elements.searchForm.length > 0) {
            elements.searchForm.off('submit.tagfilter').on('submit.tagfilter', function() {
                // The search will proceed normally, but we can add any additional logic here if needed
                // The tag will be created on the next page load when the URL contains the term
            });
        }
    };

    /**
     * Initialize the tag filter module
     */
    const init = (config = {}) => {
        const defaultConfig = {
            targetSelector: '.tag-filter-wrapper',
            primaryFormSelector: null,  // Optional: specify which form is the primary filter form
            applyBtnSelector: '.btn-apply',
            resetBtnSelector: '.btn-reset',
            tagSelectorsSelector: '.tag-select',
            searchInputSelector: 'input[name="term"]',
            searchFormSelector: 'form[name="frmSearch"]',
            useDummyData: false,
            submitOnApply: true,
            submitOnReset: true,
            submitOnRemove: true,
            closeMenuOnApply: true,
            closeMenuOnReset: true,
            onInitComplete: null
        };

        const mergedConfig = $.extend({}, defaultConfig, config);

        const $targets = $(mergedConfig.targetSelector);

        const instances = [];

        $targets.each(function() {
            const $target = $(this);

            createDomStructure(mergedConfig, $target);

            const elements = getDomElements(mergedConfig, $target);

            setupEventHandlers(elements, mergedConfig);

            populateTags(elements);

            instances.push({
                target: $target,
                elements: elements,
                config: mergedConfig
            });
        });

        if (typeof mergedConfig.onInitComplete === 'function') {
            mergedConfig.onInitComplete(instances);
        }

        return instances;
    };

    return {
        init: init,
        populateTags: function(config) {
            const elements = getDomElements(config);
            return populateTags(elements);
        }
    };
};

const TagFilterModule = createTagFilterModule(jQuery);

if (typeof window !== 'undefined' && !window.TagFilterModule) {
    try {
        window.TagFilterModule = TagFilterModule;
    } catch (e) {
        console.warn('Could not expose TagFilterModule globally:', e);
    }
}

export default TagFilterModule;


function enhanceSearchForms(config = {}) {
    const defaultConfig = {
        searchFormSelector: 'form[name="frmSearch"]',
        excludeParams: ['term'],
        excludeEmpty: true
    };

    const settings = Object.assign({}, defaultConfig, config);
    const searchForms = document.querySelectorAll(settings.searchFormSelector);

    searchForms.forEach(form => {
        form.addEventListener('submit', function(e) {
            e.preventDefault();

            const formData = new FormData(form);
            const currentParams = new URLSearchParams(window.location.search);
            const combinedParams = new URLSearchParams();

            for (const [key, value] of formData.entries()) {
                if (value && value.trim() !== '') {
                    combinedParams.append(key, value);
                }
            }

            // Preserve ALL parameters from current URL (except excluded ones)
            for (const [key, value] of currentParams.entries()) {
                if (!settings.excludeParams.includes(key) && !combinedParams.has(key)) {
                    if (!settings.excludeEmpty || (value && value !== '' && value !== '-1')) {
                        combinedParams.append(key, value);
                    }
                }
            }

            const baseAction = form.getAttribute('action') || window.location.pathname;
            const finalUrl = baseAction + (combinedParams.toString() ? '?' + combinedParams.toString() : '');

            window.location.href = finalUrl;
        });
    });
}


document.addEventListener('DOMContentLoaded', function() {
    enhanceSearchForms();
});
